import { Box, Stack } from '@mui/joy'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from 'effector-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from '~/shared/lib/logger'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { Alert } from '~/shared/ui/Alerts'
import { Button } from '~/shared/ui/Buttons'
import { UploadIcon } from '~/shared/ui/Icons'
import { Modal } from '~/shared/ui/Modal'
import { Documents } from './Documents'
import { osgopMassImportFx } from './model'

type ImportOsgopTemplateProps = {
  isShow: boolean
  onCancel: () => void
}

export function ImportOsgopTemplate({
  isShow,
  onCancel,
}: ImportOsgopTemplateProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isSubmitting = useStore(osgopMassImportFx.pending)
  const [file, setFile] = useState<File>()

  const handleFileSet = (files?: File[]) => {
    setFile(files?.[0])
  }

  const handleCancel = () => {
    setFile(undefined)
    onCancel()
  }

  const handleSave = async () => {
    if (!file) {
      snackbarEnqueued({
        messageI18n: 'alert.upload file for import',
        variant: 'warning',
      })
      return
    }

    try {
      const result = await osgopMassImportFx(file)

      snackbarEnqueued({
        message: t('alert.osgop are successfully imported', {
          processed: result.processed,
          duplicates: result.duplicates,
        }),
        variant: 'success',
      })
      await queryClient.invalidateQueries({
        queryKey: ['cars'],
      })
      handleCancel()
    } catch (e) {
      logger.error(e)
    }
  }

  return (
    <Modal
      isShow={isShow}
      onCancel={isSubmitting ? undefined : handleCancel}
      dialogProps={{
        sx: {
          minWidth: '800px',
          maxWidth: '800px',
        },
      }}
      title={t('osgop list upload in progress')}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            '.MuiGrid-direction-xs-row': {
              width: '100%',
            },
          }}
        >
          <Documents
            isShowUploadFiles
            onUploadFiles={handleFileSet}
            isSubmitting={isSubmitting}
            files={file ? [file] : []}
          />
        </Box>

        {isSubmitting && (
          <Alert variant='info'>
            {t(
              'alert.please wait until the import process is complete this may take some time',
            )}
          </Alert>
        )}
        <Stack direction='row' justifyContent='space-between' spacing={2}>
          <Button
            variant='gray'
            fullWidth
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {t('button.close')}
          </Button>
          <Button
            fullWidth
            variant='brand'
            startDecorator={<UploadIcon />}
            onClick={handleSave}
            loading={isSubmitting}
          >
            {t('button.upload osgop')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}
